import { useEffect } from 'react';
import {func, shape} from 'react-proptypes';
import {useDispatch, useSelector} from '../hooks';
import showPensionIncome from '../helpers/showPensionIncome';
import analytics from '../utility/analytics';
import {Hyperlink} from '../buttons';
import {Heading, Info, LabelText} from '../components';
import {Checkbox, CurrencyInput, Income, RadioSwitch, SplitDate} from '../inputs';
import {SYNC, sync} from '../wizard/actions';
import {FlexContainer, InputsContainer} from '../containers';

const PartnerForm = () => {
  const dispatch = useDispatch();
  const {
    output: {
      aow_age_reached,
      aow_within_ten_years,
      errors
    },
    settings: {
      entrepreneursModule
    },
    app,
    input,
  } = useSelector(state => state);
  const syncAction = sync(dispatch);

  useEffect(() => {
    dispatch({ type: SYNC, syncing: false });
    analytics.send('Navigeer', 'Partner Pagina');
  }, []);

  const onDateHandler = date => {
    showPensionIncome(app, date).then(res => {
      dispatch({
        type: SYNC,
        payload: {
          aow_age_reached: Boolean(res?.data?.aow_age_reached),
          aow_within_ten_years: Boolean(res?.data?.aow_within_ten_years)
        },
      });
    });
    syncAction('partner_dob', date);
  };

  return (
    <>
      <Heading>Je partner</Heading>
      <InputsContainer>
        <LabelText>Heb je een partner?</LabelText>
        <RadioSwitch
          name="exists"
          onChangeHandler={syncAction('applicant_has_partner')}
          checked={input.applicant_has_partner}
          errors={errors.applicant_has_partner}
        />
        {input.applicant_has_partner && (
          <>
            <LabelText>Geboortedatum partner</LabelText>
            <SplitDate name="partner_dob" onChange={onDateHandler} value={input.partner_dob} errors={errors.partner_dob} />

            {entrepreneursModule && !aow_age_reached ? (
              <>
                <LabelText>Is je partner ondernemer?</LabelText>
                <RadioSwitch
                  name="partner_is_entrepreneur"
                  onChangeHandler={syncAction('partner_is_entrepreneur')}
                  checked={input.partner_is_entrepreneur}
                  errors={errors.partner_is_entrepreneur}
                />

                {input.partner_is_entrepreneur &&
                <PartnerIsEntrepreneur input={input} sync={syncAction} errors={errors} />
                }
              </>
            ) : null}

            {(!input.partner_is_entrepreneur && (aow_within_ten_years || !aow_age_reached)) && (
              <PartnerIncome input={input} sync={syncAction} errors={errors} />
            )}

            {(aow_within_ten_years || aow_age_reached) && (
              <ParnterPensionIncome input={input} sync={syncAction} errors={errors} />
            )}
          </>
        )}
      </InputsContainer>
    </>
  );
};

const PartnerIsEntrepreneur = ({ input, sync, errors }) => (
  <InputsContainer>
    <LabelText>
      Is je partner korter dan 3 jaar ondernemer?
      <Info title="Is je partner korter dan 3 jaar ondernemer?">
        Voor de berekening van het toetsinkomen van je partner hebben we de nettowinst van de afgelopen 3 kalenderjaren
        nodig. Jaar 3 is dus vorig jaar. Is je partner nog geen 3 jaar ondernemer? Vul dan je partner&apos;s verwachte
        nettowinst in voor {new Date().getFullYear()}.
      </Info>
    </LabelText>
    <RadioSwitch
      name="partner_is_entrepreneur_for_three_years"
      onChangeHandler={() =>
        sync('partner_is_entrepreneur_for_three_years', !input.partner_is_entrepreneur_for_three_years)
      }
      checked={!input.partner_is_entrepreneur_for_three_years}
      errors={errors.partner_is_entrepreneur_for_three_years}
    />
    {input.partner_is_entrepreneur_for_three_years && (
      <>
        <LabelText>Nettowinst {new Date().getFullYear() - 3}</LabelText>
        <CurrencyInput
          id="partner_entrepreneur_fiscal_profit_three_years_ago"
          onChange={sync('partner_entrepreneur_fiscal_profit_three_years_ago')}
          value={input.partner_entrepreneur_fiscal_profit_three_years_ago}
          errors={errors.partner_entrepreneur_fiscal_profit_three_years_ago}
          placeholder="Bedrag"
        />
      </>
    )}
    <LabelText>Nettowinst {new Date().getFullYear() - 2}</LabelText>
    <CurrencyInput
      id="partner_entrepreneur_fiscal_profit_two_years_ago"
      onChange={sync('partner_entrepreneur_fiscal_profit_two_years_ago')}
      value={input.partner_entrepreneur_fiscal_profit_two_years_ago}
      errors={errors.partner_entrepreneur_fiscal_profit_two_years_ago}
      placeholder="Bedrag"
    />
    <LabelText>Nettowinst {new Date().getFullYear() - 1}</LabelText>
    <CurrencyInput
      id="partner_entrepreneur_fiscal_profit_last_year"
      onChange={sync('partner_entrepreneur_fiscal_profit_last_year')}
      value={input.partner_entrepreneur_fiscal_profit_last_year}
      errors={errors.partner_entrepreneur_fiscal_profit_last_year}
      placeholder="Bedrag"
    />
    {!input.partner_is_entrepreneur_for_three_years && (
      <>
        <LabelText>Verwachte nettowinst {new Date().getFullYear()}</LabelText>
        <CurrencyInput
          id="partner_entrepreneur_prognoses_current_year"
          onChange={sync('partner_entrepreneur_prognoses_current_year')}
          value={input.partner_entrepreneur_prognoses_current_year}
          errors={errors.partner_entrepreneur_prognoses_current_year}
          placeholder="Bedrag"
        />
      </>
    )}
  </InputsContainer>
);

PartnerIsEntrepreneur.propTypes = {
  sync: func.isRequired,
  input: shape({}).isRequired,
  errors: shape({}).isRequired,
};

const PartnerIncome = ({ input, sync, errors }) => (
  <>
    <LabelText>Bruto inkomen partner</LabelText>
    <Income
      id="partner_income"
      onAmountChangeHandler={sync('partner_income')}
      onPeriodChangeHandler={option => sync('partner_income_period', option.value)}
      amount={input.partner_income}
      period={input.partner_income_period}
      errors={errors.partner_income}
    />
    {input.partner_income_period === 'monthly' && (
      <FlexContainer>
        <Checkbox
          name="partner_holiday_bonus"
          onChange={sync('partner_holiday_bonus')}
          value={input.partner_holiday_bonus}
          label="Vakantiegeld?"
          errors={errors.partner_holiday_bonus}
        />
        <Checkbox
          name="partner_13th_month_salary"
          onChange={sync('partner_13th_month_salary')}
          value={input.partner_13th_month_salary}
          label="13e maand?"
          errors={errors.partner_13th_month_salary}
        />
      </FlexContainer>
    )}
  </>
);

PartnerIncome.propTypes = {
  sync: func.isRequired,
  input: shape({}).isRequired,
  errors: shape({}).isRequired,
};

const ParnterPensionIncome = ({ input, sync, errors }) => (
  <>
    <LabelText>
      Bruto jaarinkomen partner na AOW datum (incl. AOW)
      <Info title="Hoogte pensioen en AOW">
        Op <Hyperlink href="https://www.mijnpensioenoverzicht.nl/">mijnpensioenoverzicht.nl</Hyperlink> vind je
        informatie over je AOW en je pensioen via al je werkgevers (oud en nieuw). Let op: je vindt hier geen informatie
        over een aanvullend pensioen dat je zelf hebt geregeld.
      </Info>
    </LabelText>
    <Income
      id="partner_pension_income"
      onAmountChangeHandler={sync('partner_pension_income')}
      onPeriodChangeHandler={option => sync('partner_pension_income_period', option.value)}
      amount={input.partner_pension_income}
      period={input.partner_pension_income_period}
      errors={errors.partner_pension_income}
    />
  </>
);

ParnterPensionIncome.propTypes = {
  sync: func.isRequired,
  input: shape({}).isRequired,
  errors: shape({}).isRequired,
};

export default PartnerForm;
